import { AutoTaskManagementPageLoadable } from '@AMIEWEB/Settings/AutoTaskManagement/AutoTaskManagementPage.loadable';
import { TagsManagementPageLoadable } from '@AMIEWEB/Settings/TagsManagement/TagsManagementPage.loadable';
import { TaskOperationsPageLoadable } from '@AMIEWEB/Settings/TaskOperations/TaskOperationsPage.loadable';
import { TemplateEditorLoadable } from '@AMIEWEB/Settings/Templates/TemplateEditor.loadable';
import { UserManagementLoadable } from '@AMIEWEB/Settings/UserManagement/UserManagement.loadable';
import { EditUnitWrapperLoadable } from '@AMIEWEB/Unit/EditUnitWrapper.loadable';
import { NewUnitWrapperLoadable } from '@AMIEWEB/Unit/NewUnitWrapper.loadable';
import { UnitDetailsLandingPageLoadable } from '@AMIEWEB/Unit/UnitDetails/UnitLandingPageWrapper.loadable';
import { PageNotFound } from 'app/components/Common/PageNotFound';
import { Layout } from 'app/layout';
import { Callback } from 'oidc/Callback';
import { SignoutCallback } from 'oidc/SignoutCallback';
import { SilentRenew } from 'oidc/SilentRenew';
import { UnauthorizedLoadable } from 'oidc/Unauthorized.loadable';
import { userRoles } from 'oidc/userRoles';
import { CandidatePageLoadable } from 'pages/CandidatePage.loadable';
import * as React from 'react';
import { createBrowserRouter, Outlet } from 'react-router-dom';
import { useDecision } from '@optimizely/react-sdk';
import { PrivateRoute } from './PrivateRoute';
import { OptimizelyFlagsLoadable } from './components/Admin/OptimizelyFlags/OptimizelyFlags.loadable';
import { AppHomeLoadable } from './components/AppHome/AppHome.loadable';
import { CandidateEngagementInquiriesLoadable } from './components/Candidate/CandidateEngagement/CandidateEngagementInquiries.loadable';
import { ClientContactTaskLoadable } from './components/ClientContact/ClientContactTask.lodable';
import { NetworkErrorLoadable } from './components/Common/NetworkError/NetworkError.loadable';
import { CredentialingAttachmentsViewerLoadable } from './components/Credentialing/CredentialingAttachmentsViewer/CredentialingAttachmentsViewer.loadable';
import { FacilityDetailsLoadable } from './components/Facility/FacilityDetails/FacilityDetails.loadable';
import { SearchResultsWrapperLoadable } from './components/GlobalSearch/SearchResultWrapperLoadable';
import { OrderMapResultsLoadable } from './components/Order/OrderDetails/OrderMapResultsWrapper.loadable';
import { ReportPageLoadable } from './components/PerformanceReport/PerdformanceReport.loadable';
import { PlacementDetailsWrapperLoadable } from './components/Placement/PlacementDetails/PlacementDetailsWrapper.loadable';
import { PlacementTrackerPageLoadable } from './components/Placement/PlacementTracker/PlacementTrackerPage.loadable';
import { PlacementDocViewerLoadableClassic } from './components/Placement/PlacementDetails/PlacementTabPanel/DocumentsTab/PlacementDocViewerClassic.loadable';
import { PlacementDocViewerLoadableWeb } from './components/Placement/PlacementDetails/PlacementTabPanel/DocumentsTab/PlacementDocViewerWeb.loadable';
import { TaskManagementLoadable } from './components/Tasks/TaskManagement/TaskManagement.loadable';
import { SearchType } from './models/GlobalSearch/GlobalSearch';
import { OrderDetailsWrapperLoadable } from '@AMIEWEB/Order/OrderDetails/OrderDetailsWrapper.loadable';
import { authorizedRolesForAddEditUnit } from '@AMIEWEB/Unit/UnitDetails/Containers/unitHelper';
import { SalesReportLoadable } from '@AMIEWEB/ReportPages/R225/SalesReport.loadable';
import { NewOrderLoadable } from '@AMIEWEB/NewOrder/CreateOrder/NewOrderLoadable';
import { MarginToolLoadable } from '@AMIEWEB/MarginTool/MarginToolWrapper.loadable';
import { ff_candidate_leads_management } from './constants/FeatureFlags/Candidate/Keys';
import { RecentHistory } from './ApplicationRoot/RecentHistory';
import { AppErrorBoundaryWrapper } from './AppErrorBoundaryWrapper';

// Create a layout component that wraps routes with RecentHistory
const RootLayout = () => {
  return (
    <RecentHistory>
      <Outlet />
    </RecentHistory>
  );
};

const CandidateEngagementInquiriesRoute = () => {
  const [candidateLeadsManagement] = useDecision(ff_candidate_leads_management);

  const Component = candidateLeadsManagement?.enabled ? CandidateEngagementInquiriesLoadable : PageNotFound;

  return <PrivateRoute Component={Component} />;
};

export const RoutesV2 = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <AppErrorBoundaryWrapper />,
    children: [
      {
        index: true,
        element: <PrivateRoute Component={AppHomeLoadable} />,
      },
      {
        path: '/callback',
        element: <Callback />,
      },
      {
        path: '/health',
        element: <h3>{`The App is Healthy`}</h3>,
      },
      {
        path: '/silent_renew',
        element: <SilentRenew />,
      },
      {
        path: '/signout',
        element: <Layout Component={() => <SignoutCallback />} />,
      },
      {
        path: '/candidate/:travelerId/:brandId',
        element: <PrivateRoute Component={CandidatePageLoadable} />,
      },
      {
        path: '/placement/:placementId',
        element: <PrivateRoute Component={PlacementDetailsWrapperLoadable} />,
      },
      {
        path: '/placement/document/:documentType/:documentId',
        element: <PrivateRoute Component={PlacementDocViewerLoadableClassic} removeLayout />,
      },
      {
        path: '/placement/document/:documentId',
        element: <PrivateRoute Component={PlacementDocViewerLoadableWeb} removeLayout />,
      },
      {
        path: '/credentialing/:travelerId/:brandId/attachements/:reqId/:reqTypeId?',
        element: <PrivateRoute Component={CredentialingAttachmentsViewerLoadable} />,
      },
      {
        path: `/search/:category/:searchString?`,
        element: <PrivateRoute Component={SearchResultsWrapperLoadable} />,
      },
      {
        path: '/tasks',
        element: <PrivateRoute Component={TaskManagementLoadable} />,
      },
      {
        path: '/candidate-engagement/inquiries',
        element: <CandidateEngagementInquiriesRoute />,
      },
      {
        path: '/clientContact',
        element: <PrivateRoute Component={ClientContactTaskLoadable} />,
      },
      {
        path: '/reports/communicationAndTasks',
        element: <PrivateRoute Component={ReportPageLoadable} />,
      },
      {
        path: '/reports/recruiterSales',
        element: <PrivateRoute Component={SalesReportLoadable} />,
      },
      {
        path: '/flags',
        element: <PrivateRoute Component={OptimizelyFlagsLoadable} roles={[userRoles.all]} />,
      },
      {
        path: '/settings/user-management',
        element: <PrivateRoute Component={UserManagementLoadable} />,
      },
      {
        path: '/settings/template-editor/:editor',
        element: <PrivateRoute Component={TemplateEditorLoadable} />,
      },
      {
        path: '/settings/auto-task-management',
        element: <PrivateRoute Component={AutoTaskManagementPageLoadable} />,
      },
      {
        path: '/settings/task-operations',
        element: <PrivateRoute Component={TaskOperationsPageLoadable} />,
      },
      {
        path: '/settings/tags-management',
        element: <PrivateRoute Component={TagsManagementPageLoadable} />,
      },
      {
        path: '/order/:id',
        element: <PrivateRoute Component={OrderDetailsWrapperLoadable} />,
      },
      {
        path: '/order/:id/edit',
        element: <PrivateRoute Component={NewOrderLoadable} />,
      },
      {
        path: '/facility/:id/order/new',
        element: <PrivateRoute Component={NewOrderLoadable} />,
      },
      {
        path: '/facility/:id/newUnit',
        element: <PrivateRoute Component={NewUnitWrapperLoadable} roles={authorizedRolesForAddEditUnit} />,
      },
      {
        path: '/facility/:id/unit/:unitId',
        element: <PrivateRoute Component={UnitDetailsLandingPageLoadable} />,
      },
      {
        path: '/facility/:id/unit/:unitId/edit',
        element: <PrivateRoute Component={EditUnitWrapperLoadable} roles={authorizedRolesForAddEditUnit} />,
      },
      {
        path: '/facility/:id?',
        element: <PrivateRoute Component={FacilityDetailsLoadable} />,
      },
      {
        path: '/networkerror',
        element: <NetworkErrorLoadable />,
      },
      {
        path: '/unauthorized',
        element: <UnauthorizedLoadable />,
      },
      {
        path: '/mapresults/:view',
        element: <OrderMapResultsLoadable />,
      },
      {
        path: '/placement-tracker',
        element: <PrivateRoute Component={PlacementTrackerPageLoadable} removeLayout />,
      },
      {
        path: '/margin/:entity/:entityId/:scenarioId?',
        element: <PrivateRoute Component={MarginToolLoadable} />,
      },
      {
        path: '*',
        element: <PageNotFound />,
      },
    ],
  },
]);

